import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SEO, { PAGE_SEO } from "./SEO";
import { 
  Package, BarChart3, Target, Edit3, X, Plus,
  Sparkles, RefreshCw, CheckCircle,
  Settings, TrendingUp, Activity, ChevronDown, ChevronRight, ArrowRight,
  Zap, AlertTriangle, Brain, Cpu, Lightbulb, Camera
} from "lucide-react";
import { UserProfile, GolfClub } from "../types";
import { analyzeGapping, GapAnalysis } from "../utils/distanceCalculator";
import { recalculateFromKnownDistances } from "../utils/calculations";
import { aiService } from "../services/aiService";
import { GOLF_BRANDS, clubTypeToCategory, getModelsForBrand } from "../data/golfBrands";
import { getAllShaftModelNames } from "../data/shaftModels";
import { autoGroupClubsIntoSets, getClubsInSet } from "../utils/clubSets";
import ClubPhotoCapture, { ClubIdentification } from "./ClubPhotoCapture";
import { recordActivationIfEligible, recordProductEvent } from "../services/productEvents";

// Recalibration result modal type
interface RecalibrationResult {
  type: 'success' | 'warning' | 'error';
  title: string;
  message: string;
  clubCount?: number;
}

interface MyGolfBagProps {
  userProfile: UserProfile | null;
  onProfileUpdate: (profile: UserProfile) => Promise<boolean> | void;
}

type TabType = "bag" | "distances";

const MyGolfBag: React.FC<MyGolfBagProps> = ({ userProfile, onProfileUpdate }) => {
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') === 'distances' ? 'distances' : 'bag') as TabType;
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [gapAnalysis, setGapAnalysis] = useState<GapAnalysis | null>(null);
  const [editingClub, setEditingClub] = useState<string | null>(null);
  const [editDistance, setEditDistance] = useState<string>("");
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [showRecalcPrompt, setShowRecalcPrompt] = useState(false);
  const [recalibrationResult, setRecalibrationResult] = useState<RecalibrationResult | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pendingClubUpdate, setPendingClubUpdate] = useState<any>(null);
  const [expandedSets, setExpandedSets] = useState<Set<string>>(new Set()); // Track which sets are expanded
  
  // Club details editing states
  const [editingClubDetails, setEditingClubDetails] = useState<string | null>(null);
  const [originalClubSpecs, setOriginalClubSpecs] = useState<{loft?: number, shaftModel?: string, shaftFlex?: string, shaftType?: string} | null>(null);
  const [editClubBrand, setEditClubBrand] = useState<string>("");
  const [editClubModel, setEditClubModel] = useState<string>("");
  const [editClubCustomModel, setEditClubCustomModel] = useState<string>(""); // For "Other / Custom" model entry
  const [editClubShaftType, setEditClubShaftType] = useState<string>("");
  const [editClubShaftFlex, setEditClubShaftFlex] = useState<string>("");
  const [editClubGripType, setEditClubGripType] = useState<string>("");
  const [editGripTypeCategory, setEditGripTypeCategory] = useState<string>(""); // Tour Velvet, MCC, CP2, Wrap
  const [editLoft, setEditLoft] = useState<number | string>(""); // Editable loft for wedges
  
  // Enhanced club details editing states (Cobra DS-ADAPT level)
  const [editShaftModel, setEditShaftModel] = useState<string>("");
  const [editShaftWeight, setEditShaftWeight] = useState<string>("");
  const [editShaftTorque, setEditShaftTorque] = useState<string>("");
  const [editShaftLaunch, setEditShaftLaunch] = useState<string>("");
  const [editShaftSpin, setEditShaftSpin] = useState<string>("");
  const [editGripModel, setEditGripModel] = useState<string>("");
  const [editGripSize, setEditGripSize] = useState<string>("");
  const [editGripMaterial, setEditGripMaterial] = useState<string>("");
  const [editGripWraps, setEditGripWraps] = useState<string>("");
  const [editSwingWeight, setEditSwingWeight] = useState<string>("");
  const [editHeadVolume, setEditHeadVolume] = useState<string>("");
  const [editLoftAdjustment, setEditLoftAdjustment] = useState<string>("");
  const [editLieAdjustment, setEditLieAdjustment] = useState<string>("");
  const [editFaceAngle, setEditFaceAngle] = useState<string>("");
  const [editWeightConfig, setEditWeightConfig] = useState<string>("");

  // Add Club modal states
  const [showAddClubModal, setShowAddClubModal] = useState(false);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [photoCaptureMode, setPhotoCaptureMode] = useState<'add' | 'edit' | null>(null);
  const [newClubType, setNewClubType] = useState<string>("");
  const [newClubLoft, setNewClubLoft] = useState<string>("");
  const [newClubBrand, setNewClubBrand] = useState<string>("");
  const [newClubModel, setNewClubModel] = useState<string>("");
  const [newClubCustomModel, setNewClubCustomModel] = useState<string>(""); // For "Other / Custom" in Add Club modal

  // Shot Advisor types
  interface ShotAdviceData {
    recommendedClub: string;
    alternativeClubs: string[];
    adjustments: {
      clubSelection?: string;
      ballPosition?: string;
      stance?: string;
      swing?: string;
      aim?: string;
      [key: string]: string | undefined;
    };
    tips: string[];
    confidence: number;
  }

  // Shot Advisor states
  const [targetDistance, setTargetDistance] = useState<string>("");
  const [windSpeed, setWindSpeed] = useState<string>("no-wind");
  const [windDirection, setWindDirection] = useState<"headwind" | "tailwind" | "crosswind">("headwind");
  const [elevation, setElevation] = useState<string>("0");
  const [shotAdvice, setShotAdvice] = useState<ShotAdviceData | null>(null);
  const [isAnalyzingShot, setIsAnalyzingShot] = useState(false);
  const [analysisPhase, setAnalysisPhase] = useState(0);
  
  // REFS to guarantee fresh values are read in async functions (fixes stale closure bug)
  const targetDistanceRef = useRef(targetDistance);
  const windSpeedRef = useRef(windSpeed);
  const windDirectionRef = useRef(windDirection);
  const elevationRef = useRef(elevation);
  
  // AI Analysis animation messages
  const analysisMessages = [
    "Analyzing shot parameters...",
    "Calculating wind resistance...",
    "Evaluating elevation impact...",
    "Matching to your distances...",
    "Optimizing club selection...",
    "Generating recommendations..."
  ];
  
  // Cycle through analysis messages when analyzing
  useEffect(() => {
    if (isAnalyzingShot) {
      const interval = setInterval(() => {
        setAnalysisPhase(prev => (prev + 1) % analysisMessages.length);
      }, 500);
      return () => clearInterval(interval);
    } else {
      setAnalysisPhase(0);
    }
  }, [isAnalyzingShot, analysisMessages.length]);
  
  // Keep refs in sync with state - ensures async functions always read latest values
  useEffect(() => {
    targetDistanceRef.current = targetDistance;
  }, [targetDistance]);
  
  useEffect(() => {
    windSpeedRef.current = windSpeed;
  }, [windSpeed]);
  
  useEffect(() => {
    windDirectionRef.current = windDirection;
  }, [windDirection]);
  
  useEffect(() => {
    elevationRef.current = elevation;
  }, [elevation]);

  // Create a hash of all distances to detect any changes (not just array reference changes)
  const distanceHash = userProfile?.golfBag
    ?.map(c => `${c.type}:${c.distance || 0}`)
    .sort()
    .join(',') || '';

  useEffect(() => {
    if (userProfile?.golfBag) {
      const analysis = analyzeGapping(userProfile.golfBag);
      setGapAnalysis(analysis);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distanceHash]); // Re-analyze when ANY distance changes

  // Track that user has visited My Golf Bag (for first-time welcome flow)
  useEffect(() => {
    if (userProfile?.id) {
      localStorage.setItem(`hasVisitedMyGolfBag_${userProfile.id}`, 'true');
    }
  }, [userProfile?.id]);

  // Auto-migrate individual clubs to sets on first load
  useEffect(() => {
    if (!userProfile?.golfBag || userProfile.golfBag.length === 0) return;
    
    // Check if clubs already have set grouping
    const hasSetGrouping = userProfile.golfBag.some(c => c.setId);
    if (hasSetGrouping) return; // Already migrated
    
    // Check if we've already migrated this user (don't re-migrate on every load)
    const migrationKey = `clubs_migrated_to_sets_${userProfile.id}`;
    if (localStorage.getItem(migrationKey)) return;
    
    console.log('🔄 Auto-grouping individual clubs into sets...');
    const groupedClubs = autoGroupClubsIntoSets(userProfile.golfBag);
    
    // Check if grouping actually happened
    const hasGroups = groupedClubs.some(c => c.setId);
    if (hasGroups) {
      console.log('✅ Clubs grouped into sets, saving to profile...');
      onProfileUpdate({
        ...userProfile,
        golfBag: groupedClubs,
      });
      localStorage.setItem(migrationKey, 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.id]);

  // Distance editing handlers
  const handleDistanceEdit = (clubId: string, currentDistance?: number) => {
    setEditingClub(clubId);
    setEditDistance(currentDistance?.toString() || "");
  };

  const saveDistanceEdit = async () => {
    if (!userProfile || !editingClub) return;

    const distance = parseInt(editDistance);
    if (isNaN(distance) || distance <= 0) {
      alert('Please enter a valid distance greater than 0');
      return;
    }

    const editingClubData = userProfile.golfBag.find(c => c.id === editingClub);
    const clubName = editingClubData?.type?.toUpperCase().replace('-', ' ') || 'Club';

    const updatedGolfBag = userProfile.golfBag.map(club =>
      club.id === editingClub
        ? { 
            ...club, 
            distance, 
            isDistanceEstimated: false,
            isManuallySet: true  // CRITICAL: Mark as manually entered - won't be overwritten by recalibration
          }
        : club
    );

    console.log(`💾 Saving ${clubName} distance: ${distance} yards (isManuallySet: true)`);

    const success = await onProfileUpdate({
      ...userProfile,
      golfBag: updatedGolfBag
    });

    if (success !== false) {
      console.log(`✅ ${clubName} distance saved successfully`);
    }

    setEditingClub(null);
    setEditDistance("");
  };

  const cancelDistanceEdit = () => {
    setEditingClub(null);
    setEditDistance("");
  };

  // Live recalibration - updates related clubs when one changes
  const handleLiveRecalibration = async () => {
    if (!userProfile || !userProfile.golfBag || userProfile.golfBag.length === 0) {
      setRecalibrationResult({
        type: 'warning',
        title: 'No Clubs Found',
        message: 'Add clubs to your bag first! Go to Club Optimizer → Equipment Fitting to set up your clubs.'
      });
      return;
    }

    const clubsWithDistances = userProfile.golfBag.filter(club => club.distance);
    if (clubsWithDistances.length === 0) {
      setRecalibrationResult({
        type: 'warning',
        title: 'Set a Distance First',
        message: 'Tap the edit icon on any club and enter your actual carry distance. Then we can estimate the rest of your bag!'
      });
      return;
    }

    setIsRecalculating(true);
    
    // Track start time for minimum loading duration (3 seconds for perceived value)
    const startTime = Date.now();
    const MIN_LOADING_TIME = 3000;

    // Build known distances
    const knownDistances: { [club: string]: number } = {};
    clubsWithDistances.forEach(club => {
      if (club.distance) {
        knownDistances[club.type] = club.distance;
      }
    });

    console.log('🔄 Starting recalibration with known distances:', knownDistances);

    // Helper to apply calculated distances to golf bag
    // SIMPLE RULE: If isManuallySet === true, NEVER overwrite that distance
    const applyDistancesToBag = async (calculatedDistances: { [club: string]: number }) => {
      let updatedCount = 0;
      let preservedCount = 0;
      const preservedClubs: string[] = [];
      
      const updatedGolfBag = userProfile.golfBag.map(club => {
        // RULE 1: NEVER overwrite manually-set distances
        if (club.isManuallySet === true) {
          preservedCount++;
          preservedClubs.push(`${club.type}: ${club.distance}y`);
          console.log(`🔒 Preserving manually-set ${club.type}: ${club.distance} yards`);
          return club; // Preserve the manual distance exactly as-is
        }
        
        const calculatedDistance = calculatedDistances[club.type];
        
        // RULE 2: Only update clubs that have a calculated distance AND don't already have a manual distance
        if (calculatedDistance && calculatedDistance > 0) {
          updatedCount++;
          console.log(`📊 Updating ${club.type}: ${club.distance || 'none'} → ${Math.round(calculatedDistance)} yards`);
          return { 
            ...club, 
            distance: Math.round(calculatedDistance), 
            isDistanceEstimated: true,
            isManuallySet: false
          };
        }
        
        return club;
      });

      console.log(`📊 Recalibration summary: ${updatedCount} updated, ${preservedCount} preserved`);
      if (preservedClubs.length > 0) {
        console.log('🔒 Preserved manual distances:', preservedClubs.join(', '));
      }

      await onProfileUpdate({
        ...userProfile,
        golfBag: updatedGolfBag
      });

      return updatedCount;
    };

    let result: RecalibrationResult;

    // Try AI first, fallback to local calculation if AI fails
    try {
      const calculatedDistances = await aiService.getAIDistanceCalculation({
        userProfile,
        knownDistances,
        conditions: undefined
      });

      console.log('✅ AI returned distances:', calculatedDistances);

      if (!calculatedDistances || Object.keys(calculatedDistances).length === 0) {
        throw new Error('AI returned no distances');
      }

      const updatedCount = await applyDistancesToBag(calculatedDistances);
      result = {
        type: 'success',
        title: `Recalibrated ${updatedCount} Clubs!`,
        message: 'Your distances have been updated using AI analysis based on your profile and playing style.',
        clubCount: updatedCount
      };

    } catch (aiError) {
      console.warn("AI recalibration failed, using local calculation:", aiError);
      
      // Fallback to local calculation
      try {
        const calculatedDistances = recalculateFromKnownDistances(knownDistances);
        const updatedCount = await applyDistancesToBag(calculatedDistances);
        result = {
          type: 'success',
          title: `Recalibrated ${updatedCount} Clubs!`,
          message: 'Distances updated using standard gapping calculations based on your known distances.',
          clubCount: updatedCount
        };
      } catch (localError) {
        // Both methods failed - rare edge case
        console.error("Both AI and local recalculation failed:", localError);
        result = {
          type: 'error',
          title: 'Recalibration Failed',
          message: 'Something went wrong. Your original distances are safe. Please try again or adjust distances manually.'
        };
      }
    }
    
    // Ensure minimum loading time for perceived value
    const elapsed = Date.now() - startTime;
    if (elapsed < MIN_LOADING_TIME) {
      await new Promise(resolve => setTimeout(resolve, MIN_LOADING_TIME - elapsed));
      }
    
    // Update state - clear loading FIRST, then show modal
      setIsRecalculating(false);
    setRecalibrationResult(result);
  };

  // Club details editing handlers
  const handleClubDetailsEdit = (club: GolfClub) => {
    setEditingClubDetails(club.id);
    setEditClubBrand(club.brand || "TaylorMade");
    // Initialize distance for editing
    setEditDistance(club.distance?.toString() || "");
    
    // Check if the model is in the available models list
    const brandId = (club.brand || "TaylorMade").toLowerCase().replace(/\s+/g, '');
    const clubCategory = clubTypeToCategory(club.type);
    const availableModels = getModelsForBrand(brandId, clubCategory);
    const modelInList = availableModels.includes(club.model || "");
    
    if (club.model && !modelInList && availableModels.length > 0) {
      // Model exists but isn't in our list - treat as custom
      setEditClubModel("__OTHER__");
      setEditClubCustomModel(club.model);
    } else {
      setEditClubModel(club.model || "");
      setEditClubCustomModel("");
    }
    
    setEditClubShaftType(club.shaftType || "steel");
    setEditClubShaftFlex(club.shaftFlex || "regular");
    setEditClubGripType(club.gripType || "standard");
    setEditLoft(club.loft || ""); // Initialize editable loft
    
    // Enhanced specifications
    setEditShaftModel(club.shaftModel || "");
    setEditShaftWeight(club.shaftWeight || "");
    setEditShaftTorque(club.shaftTorque || "");
    setEditShaftLaunch(club.shaftLaunch || "mid");
    setEditShaftSpin(club.shaftSpin || "mid");
    setEditGripModel(club.gripModel || "");
    setEditGripSize(club.gripSize || "standard");
    setEditGripMaterial(club.gripMaterial || "");
    setEditGripWraps(club.gripWraps || "0 wraps");
    setEditSwingWeight(club.swingWeight || "");
    setEditHeadVolume(club.headVolume || "");
    setEditLoftAdjustment(club.adjustabilitySettings?.loftAdjustment || "Standard");
    setEditLieAdjustment(club.adjustabilitySettings?.lieAdjustment || "Standard");
    setEditFaceAngle(club.adjustabilitySettings?.faceAngle || "Square");
    setEditWeightConfig(club.adjustabilitySettings?.weightConfiguration || "Neutral");
    
    // Extract grip type category from grip model if it exists
    const gripModel = club.gripModel || "";
    if (gripModel.toLowerCase().includes('tour velvet')) {
      setEditGripTypeCategory('Tour Velvet');
    } else if (gripModel.toLowerCase().includes('mcc') || gripModel.toLowerCase().includes('multi compound')) {
      setEditGripTypeCategory('MCC');
    } else if (gripModel.toLowerCase().includes('cp2')) {
      setEditGripTypeCategory('CP2');
    } else if (gripModel.toLowerCase().includes('wrap')) {
      setEditGripTypeCategory('Wrap');
    } else {
      setEditGripTypeCategory('');
    }
    
    // Store original specs to detect changes
    // IMPORTANT: Store empty strings for undefined values to match the edit state
    setOriginalClubSpecs({
      loft: club.loft,
      shaftModel: club.shaftModel || "",
      shaftFlex: club.shaftFlex || "regular",
      shaftType: club.shaftType || "steel"
    });
  };

  const saveClubDetailsEdit = (skipRecalcPrompt: boolean = false) => {
    if (!userProfile || !editingClubDetails) return;

    const editingClub = userProfile.golfBag.find(c => c.id === editingClubDetails);
    if (!editingClub) return;

    // Check if specs that affect distance have changed
    const currentEditLoft = typeof editLoft === 'string' ? parseFloat(editLoft) : editLoft;
    // Only consider loft changed if it's a valid number and different from original
    const loftChanged = !isNaN(currentEditLoft) && currentEditLoft !== originalClubSpecs?.loft;
    const specsChanged = originalClubSpecs && (
      loftChanged ||
      editShaftModel !== originalClubSpecs.shaftModel ||
      editClubShaftFlex !== originalClubSpecs.shaftFlex ||
      editClubShaftType !== originalClubSpecs.shaftType
    );

    // Resolve the actual model value (handle "Other / Custom" case)
    const resolvedModel = editClubModel === "__OTHER__" ? editClubCustomModel : editClubModel;

    // Validate: if "Other / Custom" is selected, custom model text must not be empty
    if (editClubModel === "__OTHER__" && !editClubCustomModel.trim()) {
      alert("Please enter a model name when selecting 'Other / Custom'");
      return;
    }

    // If specs changed and we haven't shown prompt, show it
    if (specsChanged && !skipRecalcPrompt && !showRecalcPrompt) {
      setShowRecalcPrompt(true);
      setPendingClubUpdate({
        brand: editClubBrand,
        model: resolvedModel, // Use resolved model, not the raw editClubModel which could be "__OTHER__"
        shaftType: editClubShaftType,
        shaftFlex: editClubShaftFlex,
        gripType: editClubGripType,
        loft: currentEditLoft,
        shaftModel: editShaftModel,
        shaftWeight: editShaftWeight,
        shaftTorque: editShaftTorque,
        shaftLaunch: editShaftLaunch,
        shaftSpin: editShaftSpin,
        gripModel: editGripModel,
        gripTypeCategory: editGripTypeCategory,
        gripSize: editGripSize,
        gripMaterial: editGripMaterial,
        gripWraps: editGripWraps,
        swingWeight: editSwingWeight,
        headVolume: editHeadVolume,
        loftAdjustment: editLoftAdjustment,
        lieAdjustment: editLieAdjustment,
        faceAngle: editFaceAngle,
        weightConfig: editWeightConfig
      });
      return;
    }

    // Build grip model from type category if provided
    let finalGripModel = editGripModel;
    if (editGripTypeCategory && !editGripModel) {
      const brandMap: {[key: string]: string} = {
        'Tour Velvet': 'Golf Pride Tour Velvet 360',
        'MCC': 'Golf Pride MCC',
        'CP2': 'Golf Pride CP2',
        'Wrap': 'Golf Pride Tour Wrap'
      };
      finalGripModel = brandMap[editGripTypeCategory] || '';
    }

    // Use the pre-validated resolved model
    const finalClubModel = resolvedModel;

    // Check if editing a set - apply changes to all clubs in set
    const editingClubSetId = editingClub.setId;
    
    // Parse the distance if it was edited
    const editedDistanceValue = editDistance ? parseInt(editDistance) : null;
    const hasDistanceChange = editedDistanceValue && !isNaN(editedDistanceValue) && editedDistanceValue > 0 && editedDistanceValue !== editingClub.distance;
    
    const updatedGolfBag = userProfile.golfBag.map(club => {
      // Apply to this club if it's the one being edited OR if it's in the same set
      const shouldUpdate = club.id === editingClubDetails || (editingClubSetId && club.setId === editingClubSetId);
      
      // Only update loft and distance for individual clubs (not all clubs in a set)
      const shouldUpdateLoft = club.id === editingClubDetails && currentEditLoft && !isNaN(currentEditLoft);
      const shouldUpdateDistance = club.id === editingClubDetails && hasDistanceChange;
      
      return shouldUpdate
        ? { 
            ...club, 
            brand: editClubBrand,
            model: finalClubModel,
            shaftType: editClubShaftType as "graphite" | "steel",
            shaftFlex: editClubShaftFlex as "extra-stiff" | "stiff" | "regular" | "senior" | "ladies",
            gripType: editClubGripType as "standard" | "midsize" | "jumbo" | "undersize" | "corded" | "wrap",
            // Update loft only for the specific club being edited
            ...(shouldUpdateLoft && { loft: currentEditLoft }),
            // Update distance only for the specific club being edited - mark as manually set
            ...(shouldUpdateDistance && { 
              distance: editedDistanceValue, 
              isDistanceEstimated: false,
              isManuallySet: true 
            }),
            // Enhanced specifications
            shaftModel: editShaftModel || undefined,
            shaftWeight: editShaftWeight || undefined,
            shaftTorque: editShaftTorque || undefined,
            shaftLaunch: (editShaftLaunch as "low" | "mid" | "high") || undefined,
            shaftSpin: (editShaftSpin as "low" | "mid" | "high") || undefined,
            gripModel: finalGripModel || undefined,
            gripSize: (editGripSize as "undersize" | "standard" | "midsize" | "jumbo") || undefined,
            gripMaterial: editGripMaterial || undefined,
            gripWraps: editGripWraps || undefined,
            swingWeight: editSwingWeight || undefined,
            headVolume: editHeadVolume || undefined,
            adjustabilitySettings: {
              loftAdjustment: editLoftAdjustment || undefined,
              lieAdjustment: editLieAdjustment || undefined,
              faceAngle: editFaceAngle || undefined,
              weightConfiguration: editWeightConfig || undefined
            }
          }
        : club;
    });

    onProfileUpdate({
      ...userProfile,
      golfBag: updatedGolfBag
    });

    setEditingClubDetails(null);
    setShowRecalcPrompt(false);
    setPendingClubUpdate(null);
    setOriginalClubSpecs(null);
    setEditClubCustomModel(""); // Reset custom model state
  };

  const handleRecalcYes = async () => {
    if (!userProfile || !editingClubDetails) return;
    
    const editingClub = userProfile.golfBag.find(c => c.id === editingClubDetails);
    if (!editingClub) return;
    
    setIsRecalculating(true);
    
    // Step 1: Build the updated golf bag LOCALLY (don't rely on React state update)
    // This mirrors the logic in saveClubDetailsEdit but keeps it local
    const currentEditLoft = typeof editLoft === 'string' ? parseFloat(editLoft) : editLoft;
    const resolvedModel = editClubModel === "__OTHER__" ? editClubCustomModel : editClubModel;
    
    let finalGripModel = editGripModel;
    if (editGripTypeCategory && !editGripModel) {
      const brandMap: {[key: string]: string} = {
        'Tour Velvet': 'Golf Pride Tour Velvet 360',
        'MCC': 'Golf Pride MCC',
        'CP2': 'Golf Pride CP2',
        'Wrap': 'Golf Pride Tour Wrap'
      };
      finalGripModel = brandMap[editGripTypeCategory] || '';
    }
    
    const editingClubSetId = editingClub.setId;
    
    // Build updated bag with new club specs
    const updatedGolfBagWithSpecs = userProfile.golfBag.map(club => {
      const shouldUpdate = club.id === editingClubDetails || (editingClubSetId && club.setId === editingClubSetId);
      const shouldUpdateLoft = club.id === editingClubDetails && currentEditLoft && !isNaN(currentEditLoft);
      
      return shouldUpdate
        ? { 
            ...club, 
            brand: editClubBrand,
            model: resolvedModel,
            shaftType: editClubShaftType as "graphite" | "steel",
            shaftFlex: editClubShaftFlex as "extra-stiff" | "stiff" | "regular" | "senior" | "ladies",
            gripType: editClubGripType as "standard" | "midsize" | "jumbo" | "undersize" | "corded" | "wrap",
            ...(shouldUpdateLoft && { loft: currentEditLoft }),
            shaftModel: editShaftModel || undefined,
            shaftWeight: editShaftWeight || undefined,
            shaftTorque: editShaftTorque || undefined,
            shaftLaunch: (editShaftLaunch as "low" | "mid" | "high") || undefined,
            shaftSpin: (editShaftSpin as "low" | "mid" | "high") || undefined,
            gripModel: finalGripModel || undefined,
            gripSize: (editGripSize as "undersize" | "standard" | "midsize" | "jumbo") || undefined,
            gripMaterial: editGripMaterial || undefined,
            gripWraps: editGripWraps || undefined,
            swingWeight: editSwingWeight || undefined,
            headVolume: editHeadVolume || undefined,
            adjustabilitySettings: {
              loftAdjustment: editLoftAdjustment || undefined,
              lieAdjustment: editLieAdjustment || undefined,
              faceAngle: editFaceAngle || undefined,
              weightConfiguration: editWeightConfig || undefined
            }
          }
        : club;
    });
    
    // Step 2: Get known distances from the UPDATED bag
    const clubsWithDistances = updatedGolfBagWithSpecs.filter(club => club.distance);
    const knownDistances: { [club: string]: number } = {};
    clubsWithDistances.forEach(club => {
      if (club.distance) {
        knownDistances[club.type] = club.distance;
      }
    });

    // Step 3: Helper to apply calculated distances to the UPDATED bag (not the old one)
    // SIMPLE RULE: If isManuallySet === true, NEVER overwrite that distance
    const applyDistancesAndSave = (calculatedDistances: { [club: string]: number }) => {
      let updatedCount = 0;
      const finalGolfBag = updatedGolfBagWithSpecs.map(club => {
        // RULE 1: NEVER overwrite manually-set distances
        if (club.isManuallySet === true) {
          return club; // Preserve the manual distance exactly as-is
        }
        
        const calculatedDistance = calculatedDistances[club.type];
        
        // RULE 2: Only update clubs that have a calculated distance
        if (calculatedDistance && calculatedDistance > 0) {
          updatedCount++;
          return {
            ...club,
            distance: Math.round(calculatedDistance),
            isDistanceEstimated: true,
            isManuallySet: false
          };
        }
        return club;
      });

      console.log(`✅ Recalibration updated ${updatedCount} club distances (preserved manually-set distances)`);

      // Save both the updated specs AND the recalculated distances together
      onProfileUpdate({
        ...userProfile,
        golfBag: finalGolfBag
      });
    };

    // Step 4: Try AI first, fallback to local calculation if AI fails
    try {
      const calculatedDistances = await aiService.getAIDistanceCalculation({
        userProfile: userProfile,
        knownDistances,
      });

      applyDistancesAndSave(calculatedDistances);
      alert(`✅ Distances recalculated!\n\nYour club specs have been saved and distances updated.`);
    } catch (aiError) {
      console.warn("AI recalibration failed, using local calculation:", aiError);
      
      // Fallback to local calculation
      try {
        const calculatedDistances = recalculateFromKnownDistances(knownDistances);
        applyDistancesAndSave(calculatedDistances);
        alert(`✅ Club specs saved and distances updated using standard gapping calculations.`);
      } catch (localError) {
        // Both distance calculations failed - still save the club specs
        console.error("Both AI and local recalculation failed:", localError);
        onProfileUpdate({
          ...userProfile,
          golfBag: updatedGolfBagWithSpecs
        });
        alert(`⚠️ Club specs saved, but couldn't recalculate distances.\n\nYou can edit distances manually in the Distances tab.`);
      }
    } finally {
      setIsRecalculating(false);
      setEditingClubDetails(null);
      setShowRecalcPrompt(false);
      setPendingClubUpdate(null);
      setOriginalClubSpecs(null);
      setEditClubCustomModel("");
    }
  };

  const handleRecalcNo = () => {
    saveClubDetailsEdit(true);
  };

  const handleDeleteClub = (clubId: string) => {
    if (!userProfile) return;
    
    const clubToDelete = userProfile.golfBag.find(c => c.id === clubId);
    if (!clubToDelete) return;
    
    const clubName = clubToDelete.type.replace('-', ' ').toUpperCase();
    
    if (window.confirm(`Are you sure you want to remove ${clubName} from your bag?`)) {
      const updatedGolfBag = userProfile.golfBag.filter(c => c.id !== clubId);
      
      onProfileUpdate({
        ...userProfile,
        golfBag: updatedGolfBag
      });
      
      setEditingClubDetails(null);
    }
  };

  // Handle photo-identified club - pre-fill add form or update edit form
  const handlePhotoClubIdentified = (club: ClubIdentification) => {
    if (photoCaptureMode === 'edit' && editingClubDetails && userProfile) {
      const editingClub = userProfile.golfBag.find(c => c.id === editingClubDetails);
      setEditClubBrand(club.brand);
      const category = clubTypeToCategory(editingClub?.type || '7-iron');
      const models = getModelsForBrand(club.brand.toLowerCase().replace(/\s+/g, ''), category);
      if (club.model && models.includes(club.model)) {
        setEditClubModel(club.model);
        setEditClubCustomModel('');
      } else {
        setEditClubModel('__OTHER__');
        setEditClubCustomModel(club.model || '');
      }
      if (club.loft) setEditLoft(String(club.loft));
      if (club.shaft?.flex) setEditClubShaftFlex(club.shaft.flex as any);
      if (club.shaft?.type) setEditClubShaftType(club.shaft.type as any);
      setShowPhotoCapture(false);
      setPhotoCaptureMode(null);
    } else {
      setNewClubType(club.clubType);
      setNewClubBrand(club.brand);
      setNewClubModel(club.model);
      if (club.loft) setNewClubLoft(String(club.loft));
      setShowPhotoCapture(false);
      setPhotoCaptureMode(null);
      setShowAddClubModal(true);
    }
  };

  // Add new club handler
  const handleAddClub = () => {
    if (!userProfile || !newClubType) return;
    
    // Default lofts for different club types
    const defaultLofts: Record<string, number> = {
      'driver': 10.5,
      '3-wood': 15,
      '4-wood': 16.5,
      '5-wood': 18,
      '7-wood': 21,
      '9-wood': 24,
      '2-hybrid': 17,
      '3-hybrid': 20,
      '4-hybrid': 23,
      '5-hybrid': 26,
      '6-hybrid': 29,
      '7-hybrid': 32,
      '3-iron': 20,
      '4-iron': 23,
      '5-iron': 26,
      '6-iron': 29,
      '7-iron': 33,
      '8-iron': 37,
      '9-iron': 41,
      'pw': 45,
      'aw': 50,
      'gw': 52,
      'sw': 56,
      'lw': 60,
      'putter': 3
    };
    
    const loft = newClubLoft ? parseFloat(newClubLoft) : defaultLofts[newClubType] || 45;
    
    // Validate: if "Other / Custom" is selected, custom model text must not be empty
    if (newClubModel === "__OTHER__" && !newClubCustomModel.trim()) {
      alert("Please enter a model name when selecting 'Other / Custom'");
      return;
    }
    
    // Use custom model text if "Other / Custom" was selected
    const finalNewClubModel = newClubModel === "__OTHER__" ? newClubCustomModel : newClubModel;
    
    const newClub: GolfClub = {
      id: `club-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: newClubType as GolfClub['type'],
      brand: newClubBrand || 'Unknown',
      model: finalNewClubModel || '',
      shaftType: ['driver', '3-wood', '4-wood', '5-wood', '7-wood', '9-wood', '2-hybrid', '3-hybrid', '4-hybrid', '5-hybrid', '6-hybrid', '7-hybrid'].includes(newClubType) ? 'graphite' : 'steel',
      shaftFlex: 'regular',
      lie: 62,
      loft: loft,
      length: 38,
      isInBag: true
    };
    
    const updatedGolfBag = [...userProfile.golfBag, newClub];
    
    onProfileUpdate({
      ...userProfile,
      golfBag: updatedGolfBag
    });
    if (userProfile.golfBag.length === 0) {
      void recordProductEvent('GOLF_BAG_CREATED');
    }
    void recordProductEvent('CLUB_ADDED');
    void recordActivationIfEligible({
      hasCompletedOnboarding: userProfile.hasCompletedOnboarding,
      golfBag: updatedGolfBag,
      clubDistances: userProfile.clubDistances,
    });
    
    // Reset modal state
    setShowAddClubModal(false);
    setNewClubType('');
    setNewClubLoft('');
    setNewClubBrand('');
    setNewClubModel('');
    setNewClubCustomModel('');
  };

  // Shot Advisor handler - NEW APPROACH: Pass values as parameters at click time
  const getShotAdvice = async (
    inputTargetDistance: string,
    inputWindSpeed: string,
    inputWindDirection: "headwind" | "tailwind" | "crosswind",
    inputElevation: string
  ) => {
    if (!inputTargetDistance) {
      alert("🎯 Enter your target distance first!\n\nExample: If the flag is 150 yards away, type '150'");
      return;
    }

    if (!userProfile?.golfBag || userProfile.golfBag.length === 0) {
      alert("🏌️ Add clubs to get shot advice!\n\nHow to add clubs:\n1. Click 'My Bag' tab above\n2. Or go to Club Optimizer to build your full bag");
      return;
    }

    // Clear existing advice and start animation
    setShotAdvice(null);
    setIsAnalyzingShot(true);
    
    // Force render cycle and track minimum animation time
    await new Promise(resolve => setTimeout(resolve, 50));
    const startTime = Date.now();
    const MIN_ANIMATION_MS = 2500; // Show animation for at least 2.5 seconds

    try {
      const availableClubs = userProfile.golfBag.map(club => club.type);
      
      // Parse wind speed into a numeric estimate - using INPUT values passed at click time
      const windSpeedMph = inputWindSpeed === "no-wind" ? 0 :
                           inputWindSpeed === "light" ? 5 :
                           inputWindSpeed === "moderate" ? 10 :
                           inputWindSpeed === "strong" ? 15 : 20; // very-strong
      
      // Parse elevation as a number
      const elevationFeet = parseInt(inputElevation) || 0;
      
      // Build club distances array from golfBag
      const clubDistances = userProfile.golfBag
        .filter(club => club.distance && club.distance > 0)
        .map(club => ({ club: club.type, distance: club.distance! }));
      
      const shotAdviceData = await aiService.getAIShotAdvice({
        userProfile,
        targetDistance: parseInt(inputTargetDistance),
        availableClubs,
        situation: {
          lie: "fairway",
          slope: elevationFeet === 0 ? "flat" : elevationFeet > 0 ? "uphill" : "downhill",
          weather: inputWindSpeed && inputWindSpeed !== "no-wind" && inputWindSpeed !== "light" ? "windy" : "calm",
          distance: parseInt(inputTargetDistance),
          pin: "middle",
          hazards: []
        },
        clubDistances,
        windConditions: {
          speed: windSpeedMph,
          direction: inputWindDirection,
          description: windSpeedMph === 0 ? "No wind" : 
                       `${windSpeedMph} mph ${inputWindDirection}`
        },
        elevationChange: elevationFeet
      });

      // Ensure animation shows for minimum time
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_ANIMATION_MS) {
        await new Promise(resolve => setTimeout(resolve, MIN_ANIMATION_MS - elapsed));
      }

      // Store the structured advice object directly
      setShotAdvice(shotAdviceData as ShotAdviceData);
      void recordProductEvent('SHOT_ADVISOR_USED');
    } catch (error) {
      console.error("Shot advisor error:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Still wait for minimum animation time on error
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_ANIMATION_MS) {
        await new Promise(resolve => setTimeout(resolve, MIN_ANIMATION_MS - elapsed));
      }
      
      alert(`❌ Couldn't get shot advice\n\n${errorMessage}\n\nWhat to try:\n• Check your internet connection\n• Verify your clubs have distances set\n• Reload the page and try again`);
    } finally {
      setIsAnalyzingShot(false);
    }
  };

  // Show loading state instead of sign-in message to prevent race condition flicker
  if (!userProfile) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">My Golf Bag</h2>
          <p className="text-gray-600">Loading your golf bag...</p>
        </div>
      </div>
    );
  }

  // Filter out putters from distance display - golfers don't track putter carry
  const clubsWithDistances = userProfile.golfBag
    .filter(club => club.distance && club.distance > 0 && club.type !== 'putter')
    .sort((a, b) => (b.distance || 0) - (a.distance || 0));

  // Setup progress tracking
  const nonPutterClubs = userProfile.golfBag.filter(c => c.type !== 'putter');
  const distanceClubs = userProfile.golfBag.filter(c => c.distance && c.distance > 0 && c.type !== 'putter');
  const hasClubs = userProfile.golfBag.length > 0;
  const hasEnoughDistances = distanceClubs.length >= 3;
  const setupComplete = hasClubs && hasEnoughDistances;

  const setupSteps = [
    { label: "Add Clubs", done: hasClubs, detail: hasClubs ? `${userProfile.golfBag.length} clubs` : "None yet" },
    { label: "Confirm Distances", done: hasEnoughDistances, detail: `${distanceClubs.length}/${nonPutterClubs.length}` },
    { label: "Run Optimizer", done: false, detail: setupComplete ? "Ready!" : "Locked" },
  ];

  return (
    <>
      <SEO {...PAGE_SEO.myGolfBag} url="/my-golf-bag" />
    <div className="max-w-7xl mx-auto p-0 sm:p-6 space-y-0 sm:space-y-6">
      {/* Setup Progress Tracker - shown until optimizer is unlocked */}
      {!setupComplete && hasClubs && (
        <div className="mx-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b-2 sm:border-2 border-indigo-200 rounded-none sm:rounded-2xl p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-indigo-900 text-sm">Setup Progress</h3>
            <span className="text-xs text-indigo-500 font-medium">{setupSteps.filter(s => s.done).length} of {setupSteps.length} complete</span>
          </div>
          <div className="flex items-center gap-2">
            {setupSteps.map((step, i) => (
              <React.Fragment key={step.label}>
                <div className="flex-1">
                  <div className={`flex items-center gap-1.5 mb-1 ${step.done ? 'text-green-700' : 'text-gray-400'}`}>
                    {step.done ? (
                      <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 border-current flex-shrink-0" />
                    )}
                    <span className="text-xs font-semibold truncate">{step.label}</span>
                  </div>
                  <div className={`h-1.5 rounded-full ${step.done ? 'bg-green-400' : 'bg-gray-200'}`} />
                  <span className="text-[10px] text-gray-500 mt-0.5 block">{step.detail}</span>
                </div>
                {i < setupSteps.length - 1 && <div className="w-4 h-px bg-gray-300 mt-[-12px]" />}
              </React.Fragment>
            ))}
          </div>
          {hasClubs && !hasEnoughDistances && (
            <p className="text-indigo-700 text-xs mt-3">
              Switch to the <button onClick={() => setActiveTab('distances')} className="font-bold underline">Distances</button> tab to review and confirm, then head to the <Link to="/club-optimizer" className="font-bold underline">Optimizer</Link>.
            </p>
          )}
        </div>
      )}
      {/* Recalibration Result Modal */}
      {recalibrationResult && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className={`p-6 ${
              recalibrationResult.type === 'success' 
                ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                : recalibrationResult.type === 'warning'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500'
                : 'bg-gradient-to-r from-red-500 to-rose-600'
            } text-white`}>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-white/20">
                  {recalibrationResult.type === 'success' ? (
                    <Zap className="w-8 h-8" />
                  ) : recalibrationResult.type === 'warning' ? (
                    <AlertTriangle className="w-8 h-8" />
                  ) : (
                    <X className="w-8 h-8" />
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{recalibrationResult.title}</h3>
                  {recalibrationResult.clubCount !== undefined && recalibrationResult.clubCount > 0 && (
                    <p className="text-white/80 text-sm">AI-powered analysis complete</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-600 leading-relaxed">{recalibrationResult.message}</p>
              
              {recalibrationResult.type === 'success' && recalibrationResult.clubCount !== undefined && recalibrationResult.clubCount > 0 && (
                <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-100">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">All distances updated</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">Your gapping analysis will now reflect these changes.</p>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 pb-6">
              <button
                onClick={() => setRecalibrationResult(null)}
                className={`w-full py-3 px-4 rounded-xl font-semibold transition-all ${
                  recalibrationResult.type === 'success'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : recalibrationResult.type === 'warning'
                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                {recalibrationResult.type === 'success' ? 'View Updated Distances' : 'Got It'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header - App-Style on Mobile */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-none sm:rounded-2xl p-4 sm:p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Package className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold truncate">My Golf Bag</h1>
              <p className="text-blue-100 text-xs sm:text-sm md:text-base">Your clubs and distances</p>
            </div>
          </div>
          {/* Quick Nav to Club Optimizer - Made More Prominent */}
          <Link 
            to="/club-optimizer"
            className="flex items-center gap-2 px-4 py-2.5 sm:px-5 sm:py-3 bg-white text-green-700 hover:bg-green-50 rounded-xl transition-all text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl"
          >
            <Settings className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="hidden sm:inline">Optimizer</span>
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>
        </div>
      </div>

      {/* Tab Navigation - Premium App Style */}
      <div className="bg-white rounded-none sm:rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-white p-2 sm:p-3">
          <nav className="grid grid-cols-2 gap-2">
            {[
              { id: "bag", label: "My Bag", sublabel: "View Clubs", icon: Package, color: "green", gradient: "from-green-500 to-emerald-600", walkthroughId: "mybag" },
              { id: "distances", label: "Distances", sublabel: "Yardages", icon: BarChart3, color: "blue", gradient: "from-blue-500 to-cyan-600", walkthroughId: "mydistances" },
            ].map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  data-walkthrough={tab.walkthroughId || undefined}
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`relative overflow-hidden rounded-xl sm:rounded-2xl transition-all duration-300 ${
                    isActive
                      ? 'shadow-lg transform scale-105'
                      : 'shadow hover:shadow-md'
                  }`}
                  style={isActive ? {
                    background: `linear-gradient(135deg, ${tab.color === 'green' ? '#22c55e, #10b981' : tab.color === 'blue' ? '#3b82f6, #0ea5e9' : '#a855f7, #7c3aed'})`
                  } : {
                    background: 'white'
                  }}
                >
                  <div className="p-3 sm:p-4">
                    <Icon className={`w-7 h-7 sm:w-8 sm:h-8 mx-auto mb-1.5 sm:mb-2 ${
                      isActive ? 'text-white' : 'text-gray-400'
                    }`} />
                    <div className={`font-bold text-xs sm:text-sm leading-tight ${
                      isActive ? 'text-white' : 'text-gray-700'
                    }`}>
                      {tab.label}
                    </div>
                    <div className={`text-xs mt-0.5 hidden sm:block ${
                      isActive ? 'text-white/90' : 'text-gray-500'
                    }`}>
                      {tab.sublabel}
                    </div>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content - App-Style on Mobile */}
        <div className="p-4 sm:p-6">
          {/* MY BAG TAB */}
          {activeTab === "bag" && (
            <div className="space-y-3 sm:space-y-6">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Your Clubs</h2>
                <div className="text-xs sm:text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                  {userProfile.golfBag.length}/14
                </div>
              </div>

              {userProfile.golfBag && userProfile.golfBag.length > 0 ? (
                <div className="max-w-3xl mx-auto space-y-2">
                  {(() => {
                    // Filter to only show parent clubs (sets) or individual clubs (not in sets)
                    const displayClubs = userProfile.golfBag.filter(club => 
                      !club.setId || club.isSetParent
                    );
                    
                    let displayIndex = 0;
                    const results: JSX.Element[] = [];
                    
                    displayClubs.forEach((club) => {
                      const isSet = club.setId && club.isSetParent;
                      const setClubs = isSet ? getClubsInSet(userProfile.golfBag, club.setId!) : [club];
                      const clubCount = setClubs.length;
                      displayIndex++;
                      
                      // Calculate distance range for sets
                      const distances = setClubs.map(c => c.distance).filter(d => d !== undefined) as number[];
                      const minDistance = distances.length > 0 ? Math.min(...distances) : undefined;
                      const maxDistance = distances.length > 0 ? Math.max(...distances) : undefined;
                      
                      // Determine category name for sets
                      const isIronSet = isSet && setClubs.some(c => c.type.includes('iron') || c.type === 'pw');
                      const isWedgeSet = isSet && setClubs.every(c => ['pw', 'aw', 'gw', 'sw', 'lw'].includes(c.type));
                      
                      // For individual wedges, show loft next to name (e.g., "SW (56°)")
                      const isIndividualWedge = !isSet && ['pw', 'aw', 'gw', 'sw', 'lw'].includes(club.type);
                      const categoryName = isIronSet 
                        ? 'Irons' 
                        : isWedgeSet 
                          ? 'Wedges' 
                          : isIndividualWedge && club.loft 
                            ? `${club.type.toUpperCase()} (${club.loft}°)`
                            : club.type.replace('-', ' ').toUpperCase();
                      const setDetails = isSet ? (club.setName || `${setClubs[0].type.toUpperCase()}-${setClubs[setClubs.length-1].type.toUpperCase()}`) : null;
                      
                      const isExpanded = expandedSets.has(club.setId || club.id);
                      
                      // Parent row for set (or single club row)
                      results.push(
                        <div key={club.id}>
                          <div 
                            onClick={(e) => {
                              if (isSet) {
                                // Toggle expansion
                                e.stopPropagation();
                                const newExpanded = new Set(expandedSets);
                                if (isExpanded) {
                                  newExpanded.delete(club.setId!);
                                } else {
                                  newExpanded.add(club.setId!);
                                }
                                setExpandedSets(newExpanded);
                              } else {
                                // Edit individual club
                                handleClubDetailsEdit(club);
                              }
                            }}
                            className="bg-white rounded-xl p-4 border-2 border-gray-200 hover:border-green-400 hover:shadow-lg transition-all cursor-pointer group"
                          >
                            <div className="flex items-center justify-between gap-4">
                              {/* Left: Club/Set Name & Brand */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                  <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                    {displayIndex}
                                  </div>
                                  {isSet && (
                                    <div className="flex-shrink-0">
                                      {isExpanded ? (
                                        <ChevronDown className="w-5 h-5 text-gray-600" />
                                      ) : (
                                        <ChevronRight className="w-5 h-5 text-gray-600" />
                                      )}
                                    </div>
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <h3 className="font-bold text-base text-gray-900">
                                      {categoryName}
                                    </h3>
                                    <div className="text-sm">
                                      {isSet && setDetails ? (
                                        <div className="text-gray-500">{setDetails}</div>
                                      ) : null}
                                      {club.brand && club.model ? (
                                        <div className="text-gray-600">
                                          {`${club.brand} ${club.model}`}
                                          {isSet && <span className="ml-2 text-xs text-gray-500">({clubCount} clubs)</span>}
                                        </div>
                                      ) : (
                                        <span className="text-orange-500 italic">Tap to add details</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Middle: Specs */}
                              <div className="hidden sm:flex items-center gap-3 text-sm text-gray-600">
                                <span className="capitalize">{club.shaftType}</span>
                                <span className="text-gray-400">•</span>
                                <span className="capitalize">{club.shaftFlex}</span>
                              </div>
                              
                              {/* Right: Distance - hide for putters */}
                              {club.type !== 'putter' && (club.distance || (minDistance && maxDistance)) && (
                              <div className="flex items-center gap-2">
                                <div className="text-right">
                                  {isSet && minDistance && maxDistance ? (
                                    <div className="text-xl font-bold text-green-600">
                                      {maxDistance}-{minDistance}
                                      <span className="text-sm text-gray-500 ml-0.5">y</span>
                                    </div>
                                  ) : club.distance ? (
                                    <div className="text-2xl font-bold text-green-600">
                                      {club.distance}
                                      <span className="text-sm text-gray-500 ml-0.5">y</span>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            )}
                              
                              {/* Edit Icon/Button */}
                              {isSet ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClubDetailsEdit(club);
                                  }}
                                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 transition-colors flex items-center gap-1"
                                >
                                  <Edit3 className="w-3.5 h-3.5" />
                                  Edit Set
                                </button>
                              ) : (
                                <div className="text-gray-400 group-hover:text-green-600 transition-colors">
                                  <Edit3 className="w-5 h-5" />
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Expanded individual clubs in set */}
                          {isSet && isExpanded && (
                            <div className="ml-8 mt-1 space-y-1">
                              {setClubs.map((setClub, idx) => (
                                <div
                                  key={setClub.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClubDetailsEdit(setClub);
                                  }}
                                  className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-green-300 hover:bg-gray-100 transition-all cursor-pointer group"
                                >
                                  <div className="flex items-center justify-between gap-4">
                                    <div className="flex-1">
                                      <h4 className="font-medium text-sm text-gray-900 capitalize">
                                        {setClub.type.replace('-', ' ').toUpperCase()}
                                        {['pw', 'aw', 'gw', 'sw', 'lw'].includes(setClub.type) && setClub.loft && (
                                          <span className="text-gray-600 font-normal"> ({setClub.loft}°)</span>
                                        )}
                                      </h4>
                                      <div className="text-xs text-gray-500">
                                        {!['pw', 'aw', 'gw', 'sw', 'lw'].includes(setClub.type) && setClub.loft && `${setClub.loft}° • `}{setClub.shaftType}
                                      </div>
                                    </div>
                                    
                                    {setClub.distance && (
                                      <div className="text-right">
                                        <div className="text-lg font-bold text-green-600">
                                          {setClub.distance}
                                          <span className="text-xs text-gray-500 ml-0.5">y</span>
                                        </div>
                                      </div>
                                    )}
                                    
                                    <div className="text-gray-400 group-hover:text-green-600 transition-colors">
                                      <Edit3 className="w-4 h-4" />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    });
                    
                    return results;
                  })()}
                </div>
              ) : (
                <div className="text-center py-10 px-4">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center mx-auto mb-5">
                    <Package className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Let's Build Your Bag</h3>
                  <p className="text-gray-600 mb-6 max-w-sm mx-auto">
                    Add your clubs one by one, or snap a photo to auto-identify them. This is the foundation for every recommendation.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-sm mx-auto">
                    <button
                      onClick={() => setShowAddClubModal(true)}
                      className="flex-1 inline-flex items-center justify-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-colors font-semibold"
                    >
                      <Plus className="w-5 h-5" />
                      <span>Add a Club</span>
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-4">
                    If you completed the quiz and don't see clubs, try <button onClick={() => window.location.reload()} className="underline hover:text-gray-600">refreshing the page</button>.
                  </p>
                </div>
              )}

              {/* Add Club Button - Always visible at bottom when bag has clubs */}
              {userProfile.golfBag && userProfile.golfBag.length > 0 && (
                <div className="max-w-3xl mx-auto mt-4">
                  {userProfile.golfBag.length >= 14 ? (
                    <div className="w-full flex flex-col items-center gap-2 py-3 px-4 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
                      <div className="flex items-center gap-2 text-gray-500">
                        <Package className="w-5 h-5" />
                        <span className="font-semibold">Bag Full ({userProfile.golfBag.length}/14)</span>
                      </div>
                      <p className="text-xs text-gray-400 text-center">Remove a club to add a new one</p>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAddClubModal(true)}
                      className="w-full flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-green-400 rounded-xl text-green-600 hover:bg-green-50 hover:border-green-500 transition-all font-semibold"
                    >
                      <Plus className="w-5 h-5" />
                      Add Club ({userProfile.golfBag.length}/14)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* DISTANCES TAB - App-Style on Mobile */}
          {activeTab === "distances" && (
            <div className="space-y-3 sm:space-y-6">
              {/* Live Recalibration Button - Compact on Mobile */}
              {clubsWithDistances.length > 0 && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-l-4 border-purple-500 p-3 sm:p-4 rounded-r-lg">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <h3 className="font-semibold text-sm text-gray-900">Live Recalibration</h3>
                        <p className="text-xs text-gray-600 truncate">Update one, AI adjusts all</p>
                      </div>
                    </div>
                    <button
                      onClick={handleLiveRecalibration}
                      disabled={isRecalculating}
                      className={`w-full sm:w-auto flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                        isRecalculating
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-md'
                      }`}
                    >
                      {isRecalculating ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Updating...</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          <span>Recalibrate</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Gapping Score - Compact on Mobile */}
              {gapAnalysis && (
                <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg sm:rounded-xl p-3 sm:p-6 min-w-0">
                  <div className="flex items-start justify-between gap-3 min-w-0">
                    <div className="min-w-0 pr-2">
                      <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Gapping Score</h2>
                      <p className="text-xs sm:text-sm text-gray-600 mt-0.5">Club spacing quality</p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-3xl sm:text-4xl font-bold text-green-600 leading-none">{gapAnalysis.gappingScore}</div>
                      <div className="text-xs text-gray-500 mt-1">/100</div>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          gapAnalysis.gappingScore >= 80 ? 'bg-green-500' :
                          gapAnalysis.gappingScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${gapAnalysis.gappingScore}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Distance Chart */}
              {clubsWithDistances.length > 0 ? (
                <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 min-w-0 overflow-hidden">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                    <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 mr-2 flex-shrink-0" />
                    Club Distances
                  </h2>
                  
                  <div className="space-y-3 min-w-0">
                    {clubsWithDistances.map((club, index) => {
                      const isEditing = editingClub === club.id;
                      const nextClub = clubsWithDistances[index + 1];
                      const gap = nextClub ? (club.distance || 0) - (nextClub.distance || 0) : null;
                      
                      return (
                        <div key={club.id} className="space-y-2">
                          <div className="flex flex-col gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors min-w-0 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex gap-3 min-w-0 flex-1 items-start">
                              <div className="w-16 sm:w-24 flex-shrink-0 text-xs sm:text-sm font-medium text-gray-700 uppercase leading-tight">
                                {club.type}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-gray-900 break-words">
                                  {club.brand && club.model ? (
                                    `${club.brand} ${club.model}`
                                  ) : (
                                    <span className="text-orange-500 italic">Add brand & model</span>
                                  )}
                                </div>
                                <div className="text-xs sm:text-sm text-gray-500 mt-0.5 flex flex-wrap gap-x-1.5 gap-y-0.5">
                                  <span>{club.loft}°</span>
                                  <span className="text-gray-300">•</span>
                                  <span className="capitalize">{club.shaftType}</span>
                                  <span className="text-gray-300">•</span>
                                  <span className="capitalize">{club.shaftFlex}</span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between gap-3 border-t border-gray-200/80 pt-3 sm:border-0 sm:pt-0 sm:justify-end sm:flex-shrink-0">
                              {isEditing ? (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <input
                                    type="number"
                                    value={editDistance}
                                    onChange={(e) => setEditDistance(e.target.value)}
                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                                    placeholder="yards"
                                    autoFocus
                                  />
                                  <button
                                    onClick={saveDistanceEdit}
                                    className="text-green-600 hover:text-green-700"
                                  >
                                    <CheckCircle className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={cancelDistanceEdit}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    <X className="w-5 h-5" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
                                  <div className="text-left sm:text-right min-w-0">
                                    <div className="text-xl sm:text-2xl font-bold text-gray-900">
                                      {club.distance}
                                      <span className="text-sm text-gray-500 ml-1">yds</span>
                                    </div>
                                    <div className={`text-xs mt-0.5 ${club.isDistanceEstimated ? 'text-yellow-600' : 'text-green-600'}`}>
                                      {club.isDistanceEstimated ? 'Estimated' : 'Measured'}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleDistanceEdit(club.id, club.distance)}
                                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                                    title="Edit distance"
                                  >
                                    <Edit3 className="w-5 h-5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {gap !== null && (
                            <div className={`text-center py-1 text-sm ${
                              gap < 5 ? 'text-red-600' : gap > 20 ? 'text-orange-600' : 'text-green-600'
                            }`}>
                              {gap}yd gap
                              {gap < 5 && ' (too close)'}
                              {gap > 20 && ' (too large)'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Go to Club Optimizer CTA */}
                  {gapAnalysis && (gapAnalysis.overlaps.length > 0 || gapAnalysis.gaps.length > 0) && (
                    <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">Gapping Issues Detected</h4>
                          <p className="text-sm text-gray-600">
                            {gapAnalysis.overlaps.length} overlaps, {gapAnalysis.gaps.length} large gaps
                          </p>
                        </div>
                        <a
                          href="/club-optimizer"
                          className="flex items-center space-x-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                        >
                          <TrendingUp className="w-4 h-4" />
                          <span>Optimize Bag</span>
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Distances Set</h3>
                  <p className="text-gray-600">Set distances for your clubs to see analysis</p>
                </div>
              )}
            </div>
          )}

          {/* Shot Advisor removed - now lives in Club Optimizer Hub */}
          {false as any && (
            <div className="hidden">placeholder
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Input Section */}
                <div className="card p-6 space-y-4">
                  <h3 className="font-semibold text-gray-900">Shot Details</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Distance (yards)
                    </label>
                    <input
                      type="number"
                      value={targetDistance}
                      onChange={(e) => setTargetDistance(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g., 150"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Wind Speed
                      </label>
                      <select
                        value={windSpeed}
                        onChange={(e) => setWindSpeed(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">No wind (0 mph)</option>
                        <option value="5">Light breeze (0-5 mph)</option>
                        <option value="10">Moderate wind (5-10 mph)</option>
                        <option value="15">Strong wind (10-15 mph)</option>
                        <option value="20">Very strong (15-20 mph)</option>
                        <option value="25">Extreme wind (20+ mph)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Wind Direction
                      </label>
                      <select
                        value={windDirection}
                        onChange={(e) => setWindDirection(e.target.value as any)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="headwind">Headwind</option>
                        <option value="tailwind">Tailwind</option>
                        <option value="crosswind">Crosswind</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Elevation Change
                    </label>
                    <select
                      value={elevation}
                      onChange={(e) => setElevation(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="0">Level (0 ft)</option>
                      <option value="5">Slightly uphill (+5 ft)</option>
                      <option value="10">Uphill (+10 ft)</option>
                      <option value="20">Significantly uphill (+20 ft)</option>
                      <option value="-5">Slightly downhill (-5 ft)</option>
                      <option value="-10">Downhill (-10 ft)</option>
                      <option value="-20">Significantly downhill (-20 ft)</option>
                    </select>
                  </div>

                  <button
                    onClick={() => getShotAdvice(targetDistance, windSpeed, windDirection, elevation)}
                    className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold"
                  >
                    Get Shot Advice
                  </button>
                </div>

                {/* Advice Display */}
                <div className={`card ${shotAdvice || isAnalyzingShot ? 'p-0 overflow-hidden' : 'p-6'}`}>
                  {!shotAdvice && !isAnalyzingShot && (
                  <h3 className="font-semibold text-gray-900 mb-4">Recommendation</h3>
                  )}
                  {isAnalyzingShot ? (
                    /* Futuristic AI Analysis Animation */
                    <div className="relative overflow-hidden min-h-[300px] flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                      {/* Background Grid Effect */}
                      <div 
                        className="absolute inset-0 opacity-20"
                        style={{
                          backgroundImage: `
                            linear-gradient(rgba(34, 197, 94, 0.3) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(34, 197, 94, 0.3) 1px, transparent 1px)
                          `,
                          backgroundSize: '20px 20px',
                          animation: 'pulse 2s ease-in-out infinite'
                        }}
                      />
                      
                      {/* Scanning Lines */}
                      <div 
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: 'linear-gradient(transparent 0%, rgba(34, 197, 94, 0.15) 50%, transparent 100%)',
                          backgroundSize: '100% 200%',
                          animation: 'scanline 1.5s linear infinite'
                        }}
                      />
                      
                      {/* Central Brain Icon with Glow */}
                      <div className="relative mb-6">
                        {/* Outer Pulse Ring */}
                        <div 
                          className="absolute inset-0 rounded-full bg-green-500"
                          style={{
                            transform: 'scale(2.5)',
                            opacity: 0.1,
                            animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite'
                          }}
                        />
                        {/* Middle Pulse Ring */}
                        <div 
                          className="absolute inset-0 rounded-full bg-green-400"
                          style={{
                            transform: 'scale(1.8)',
                            opacity: 0.2,
                            animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite 0.3s'
                          }}
                        />
                        {/* Brain Container */}
                        <div 
                          className="relative w-20 h-20 rounded-full flex items-center justify-center"
                          style={{
                            background: 'linear-gradient(135deg, #22c55e 0%, #10b981 50%, #059669 100%)',
                            boxShadow: '0 0 40px rgba(34, 197, 94, 0.5), 0 0 80px rgba(34, 197, 94, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.2)'
                          }}
                        >
                          <Brain size={40} className="text-white" style={{ animation: 'pulse 1s ease-in-out infinite' }} />
                        </div>
                        {/* Orbiting Dots */}
                        <div 
                          className="absolute inset-0"
                          style={{ animation: 'spin 3s linear infinite' }}
                        >
                          <div className="absolute -top-2 left-1/2 w-2 h-2 bg-green-400 rounded-full" style={{ transform: 'translateX(-50%)', boxShadow: '0 0 10px rgba(34, 197, 94, 0.8)' }} />
                        </div>
                        <div 
                          className="absolute inset-0"
                          style={{ animation: 'spin 3s linear infinite reverse' }}
                        >
                          <div className="absolute -bottom-2 left-1/2 w-2 h-2 bg-emerald-300 rounded-full" style={{ transform: 'translateX(-50%)', boxShadow: '0 0 10px rgba(52, 211, 153, 0.8)' }} />
                        </div>
                      </div>
                      
                      {/* Status Text */}
                      <div className="text-center relative z-10 mb-4">
                        <h4 
                          className="text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-500 bg-clip-text text-transparent mb-2"
                          style={{ letterSpacing: '0.05em' }}
                        >
                          AI ANALYSIS IN PROGRESS
                        </h4>
                        <p 
                          className="text-green-600 font-medium text-sm"
                          style={{ 
                            minHeight: '20px',
                            transition: 'all 0.3s ease-in-out'
                          }}
                        >
                          {analysisMessages[analysisPhase]}
                        </p>
                      </div>
                      
                      {/* Progress Indicators */}
                      <div className="flex items-center gap-2 relative z-10 mb-4">
                        {analysisMessages.map((_, index) => (
                          <div 
                            key={index}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${
                              index <= analysisPhase 
                                ? 'bg-green-500 scale-110' 
                                : 'bg-gray-300 scale-100'
                            }`}
                            style={{
                              boxShadow: index <= analysisPhase ? '0 0 8px rgba(34, 197, 94, 0.6)' : 'none'
                            }}
                          />
                        ))}
                      </div>
                      
                      {/* Data Points Animation */}
                      <div className="flex items-center gap-4 text-xs text-gray-500 relative z-10">
                        <div className="flex items-center gap-1">
                          <Activity size={12} className="text-green-500" style={{ animation: 'pulse 0.8s ease-in-out infinite' }} />
                          <span>Wind: {windSpeed === 'no-wind' ? '0' : windSpeed} mph</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Zap size={12} className="text-amber-500" style={{ animation: 'pulse 0.8s ease-in-out infinite 0.2s' }} />
                          <span>Elev: {elevation}ft</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Cpu size={12} className="text-blue-500" style={{ animation: 'pulse 0.8s ease-in-out infinite 0.4s' }} />
                          <span>{targetDistance}yd</span>
                        </div>
                      </div>
                      
                      {/* CSS Keyframes */}
                      <style>{`
                        @keyframes scanline {
                          0% { background-position: 0% 0%; }
                          100% { background-position: 0% 100%; }
                        }
                        @keyframes spin {
                          from { transform: rotate(0deg); }
                          to { transform: rotate(360deg); }
                        }
                        @keyframes ping {
                          75%, 100% { transform: scale(2.5); opacity: 0; }
                        }
                      `}</style>
                    </div>
                  ) : shotAdvice ? (
                    <div className="overflow-hidden">
                      {/* Hero Section with Gradient */}
                      <div className="bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 p-6 text-white relative overflow-hidden">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                          <div className="absolute inset-0" style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                          }} />
                        </div>
                        
                        <div className="relative">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                                <Target size={20} className="text-white" />
                              </div>
                              <h3 className="text-xl font-bold">Shot Recommendation</h3>
                            </div>
                            <button
                              onClick={() => setShotAdvice(null)}
                              className="px-3 py-1.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg transition-colors flex items-center gap-1 text-sm font-medium"
                            >
                              ← New Shot
                            </button>
                          </div>
                          
                          {/* Main Club Recommendation */}
                          <div className="flex items-center gap-4 mt-6">
                            <div className="bg-white text-green-700 px-6 py-3 rounded-2xl font-bold text-2xl shadow-lg">
                              {shotAdvice.recommendedClub.replace(/-/g, ' ').toUpperCase()}
                            </div>
                            <div className="text-white/90">
                              <div className="text-sm font-medium">Target Distance</div>
                              <div className="text-2xl font-bold">{targetDistance} yards</div>
                            </div>
                          </div>
                          
                          {/* Alternative Clubs */}
                          {shotAdvice.alternativeClubs.length > 0 && (
                            <div className="mt-4 flex items-center gap-2">
                              <span className="text-white/70 text-sm">Alternatives:</span>
                              <div className="flex gap-2">
                                {shotAdvice.alternativeClubs.map((club) => (
                                  <span
                                    key={club}
                                    className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-sm font-medium"
                                  >
                                    {club.replace(/-/g, ' ').toUpperCase()}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Setup & Tips Section */}
                      <div className="p-6 bg-gradient-to-b from-gray-50 to-white">
                        {/* Setup Adjustments */}
                        <div className="mb-6">
                          <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                              <Settings size={16} className="text-emerald-600" />
                            </div>
                            Setup Adjustments
                          </h4>
                          <div className="grid gap-3">
                            {Object.entries(shotAdvice.adjustments).map(
                              ([key, value]) => value && (
                                <div
                                  key={key}
                                  className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                                >
                                  <div className="font-semibold text-gray-900 capitalize text-sm mb-1">
                                    {key.replace(/([A-Z])/g, " $1")}
                                  </div>
                                  <div className="text-gray-600 text-sm">
                                    {value}
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>

                        {/* Pro Tips */}
                        {shotAdvice.tips.length > 0 && (
                          <div>
                            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                                <Lightbulb size={16} className="text-amber-600" />
                              </div>
                              Pro Tips
                            </h4>
                            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
                              <div className="space-y-3">
                                {shotAdvice.tips.map((tip, index) => (
                                  <div
                                    key={index}
                                    className="flex items-start gap-3"
                                  >
                                    <div className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                                      {index + 1}
                                    </div>
                                    <p className="text-gray-700 text-sm">{tip}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Target className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p>Enter shot details to get AI-powered advice</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {/* Enhanced Club Details Edit Modal - Cobra DS-ADAPT Level */}
      {editingClubDetails && (() => {
        const editingClub = userProfile?.golfBag.find(c => c.id === editingClubDetails);
        const clubCategory = editingClub ? clubTypeToCategory(editingClub.type) : 'irons';
        const availableModels = getModelsForBrand(editClubBrand.toLowerCase().replace(/\s+/g, ''), clubCategory);
        
        // Check if editing a set
        const isEditingSet = editingClub?.setId;
        const setClubs = isEditingSet ? getClubsInSet(userProfile.golfBag, editingClub.setId!) : [editingClub];
        const setClubCount = setClubs.length;
        
        return (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
            onClick={(e) => e.target === e.currentTarget && setEditingClubDetails(null)}
          >
            <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold mb-2 text-gray-900 flex items-center gap-2">
                <Settings className="w-6 h-6 text-green-600" />
                {isEditingSet 
                  ? `Edit ${editingClub?.setName || 'Set'}`
                  : `Edit ${editingClub?.type.replace('-', ' ').toUpperCase()} Details`
                }
              </h3>
              {isEditingSet && (
                <p className="text-sm text-gray-600 mb-4 ml-8">
                  Changes apply to all {setClubCount} clubs in this set
                </p>
              )}
              
              <div className="space-y-6">
                {/* DISTANCE - Prominent at the top */}
                {!isEditingSet && (
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-300 rounded-xl p-4">
                    <h4 className="font-semibold text-lg text-green-800 mb-2 flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      Distance
                    </h4>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={editDistance}
                            onChange={(e) => setEditDistance(e.target.value)}
                            className="w-32 px-4 py-3 text-2xl font-bold border-2 border-green-400 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-center"
                            placeholder="0"
                            min="0"
                            max="400"
                          />
                          <span className="text-xl font-semibold text-gray-600">yards</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {editingClub?.isManuallySet 
                            ? "✓ Manually set - won't be changed by recalibration" 
                            : "Enter your actual distance for this club"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Basic Information */}
                <div className="border-b border-gray-200 pb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-lg text-gray-800">Basic Information</h4>
                    {!isEditingSet && (
                      <button
                        type="button"
                        onClick={() => {
                          setPhotoCaptureMode('edit');
                          setShowPhotoCapture(true);
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        <Camera className="w-4 h-4" />
                        Snap photo to update
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                      <select
                        value={editClubBrand}
                        onChange={(e) => {
                          setEditClubBrand(e.target.value);
                          // Reset model when brand changes
                          setEditClubModel("");
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="">Select Brand</option>
                        {GOLF_BRANDS.map(brand => (
                          <option key={brand.id} value={brand.name}>
                            {brand.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                      {availableModels.length > 0 ? (
                        <>
                          <select
                            value={editClubModel}
                            onChange={(e) => {
                              setEditClubModel(e.target.value);
                              if (e.target.value !== "__OTHER__") {
                                setEditClubCustomModel(""); // Clear custom when selecting a listed model
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          >
                            <option value="">Select Model</option>
                            {availableModels.map(model => (
                              <option key={model} value={model}>
                                {model}
                              </option>
                            ))}
                            <option value="__OTHER__">Other / Custom</option>
                          </select>
                          {editClubModel === "__OTHER__" && (
                            <input
                              type="text"
                              value={editClubCustomModel}
                              onChange={(e) => setEditClubCustomModel(e.target.value)}
                              className="w-full px-3 py-2 mt-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              placeholder="Enter model name (e.g., AP2, Eye2, M3)"
                              autoFocus
                            />
                          )}
                        </>
                      ) : (
                        <input
                          type="text"
                          value={editClubModel}
                          onChange={(e) => setEditClubModel(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          placeholder="Enter model name"
                        />
                      )}
                    </div>

                    {/* Loft - Show for all clubs but especially important for wedges */}
                    {editingClub && !isEditingSet && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Loft (degrees)
                          {['pw', 'aw', 'gw', 'sw', 'lw'].includes(editingClub.type) && (
                            <span className="text-green-600 ml-1 text-xs">*Important for wedges</span>
                          )}
                        </label>
                        {['pw', 'aw', 'gw', 'sw', 'lw'].includes(editingClub.type) ? (
                          <select
                            value={editLoft}
                            onChange={(e) => setEditLoft(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          >
                            <option value="">Select Loft</option>
                            {[44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 64].map(loft => (
                              <option key={loft} value={loft}>
                                {loft}°
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="number"
                            value={editLoft}
                            onChange={(e) => setEditLoft(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            placeholder="e.g., 10.5"
                            step="0.5"
                            min="0"
                            max="70"
                          />
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {['pw', 'aw', 'gw', 'sw', 'lw'].includes(editingClub.type) 
                            ? 'Accurate loft helps with gapping and recommendations'
                            : 'Enter the club\'s stated loft'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Shaft Specifications */}
                <div className="border-b border-gray-200 pb-4">
                <h4 className="font-semibold text-lg text-gray-800 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Shaft Specifications
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shaft Model</label>
                    {editingClub && (editingClub.type === 'driver' || editingClub.type.includes('wood') || editingClub.type.includes('hybrid')) ? (
                      <select
                        value={editShaftModel}
                        onChange={(e) => {
                          setEditShaftModel(e.target.value);
                          // Auto-populate weight/torque if available from shaft database
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select Shaft Model</option>
                        {getAllShaftModelNames(editingClub.type).map(shaftName => (
                          <option key={shaftName} value={shaftName}>
                            {shaftName}
                          </option>
                        ))}
                        <option value="custom">Other / Custom</option>
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={editShaftModel}
                        onChange={(e) => setEditShaftModel(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., KBS PGI, Project X HZRDUS"
                      />
                    )}
                    {editingClub && (editingClub.type === 'driver' || editingClub.type.includes('wood') || editingClub.type.includes('hybrid')) && (
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Most common shafts for {editClubBrand} {editClubModel || 'this club'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={editClubShaftType}
                      onChange={(e) => setEditClubShaftType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="steel">Steel</option>
                      <option value="graphite">Graphite</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Flex</label>
                    <select
                      value={editClubShaftFlex}
                      onChange={(e) => setEditClubShaftFlex(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="ladies">Ladies</option>
                      <option value="senior">Senior</option>
                      <option value="regular">Regular</option>
                      <option value="stiff">Stiff</option>
                      <option value="extra-stiff">Extra Stiff</option>
                    </select>
                  </div>

                  {/* Advanced shaft fields - Only for Drivers/Woods/Hybrids */}
                  {editingClub && (editingClub.type === 'driver' || editingClub.type.includes('wood') || editingClub.type.includes('hybrid')) && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Weight</label>
                        <input
                          type="text"
                          value={editShaftWeight}
                          onChange={(e) => setEditShaftWeight(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 85g, 70g"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Torque</label>
                        <input
                          type="text"
                          value={editShaftTorque}
                          onChange={(e) => setEditShaftTorque(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 3.5°, 4.2°"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Launch</label>
                        <select
                          value={editShaftLaunch}
                          onChange={(e) => setEditShaftLaunch(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Not specified</option>
                          <option value="low">Low</option>
                          <option value="mid">Mid</option>
                          <option value="high">High</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Spin</label>
                        <select
                          value={editShaftSpin}
                          onChange={(e) => setEditShaftSpin(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Not specified</option>
                          <option value="low">Low</option>
                          <option value="mid">Mid</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                  {/* Simplified shaft weight for Irons/Wedges */}
                  {editingClub && !editingClub.type.includes('wood') && !editingClub.type.includes('hybrid') && editingClub.type !== 'driver' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Shaft Weight Range</label>
                      <select
                        value={editShaftWeight}
                        onChange={(e) => setEditShaftWeight(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Not specified</option>
                        <option value="Light (< 90g)">Light (&lt; 90g)</option>
                        <option value="Mid (90-110g)">Mid (90-110g)</option>
                        <option value="Heavy (> 110g)">Heavy (&gt; 110g)</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Grip Specifications */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="font-semibold text-lg text-gray-800 mb-3 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-purple-600" />
                  Grip Specifications
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grip Type</label>
                    <select
                      value={editGripTypeCategory}
                      onChange={(e) => {
                        setEditGripTypeCategory(e.target.value);
                        // Auto-populate grip model if type selected
                        if (e.target.value) {
                          const brandMap: {[key: string]: string} = {
                            'Tour Velvet': 'Golf Pride Tour Velvet 360',
                            'MCC': 'Golf Pride MCC',
                            'CP2': 'Golf Pride CP2',
                            'Wrap': 'Golf Pride Tour Wrap'
                          };
                          setEditGripModel(brandMap[e.target.value] || '');
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">Select Grip Type</option>
                      <option value="Tour Velvet">Tour Velvet</option>
                      <option value="MCC">MCC (Multi-Compound)</option>
                      <option value="CP2">CP2</option>
                      <option value="Wrap">Wrap</option>
                      <option value="custom">Other / Custom</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Most common grip types
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grip Model</label>
                    <input
                      type="text"
                      value={editGripModel}
                      onChange={(e) => setEditGripModel(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="e.g., Golf Pride Tour Velvet 360"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                    <select
                      value={editGripSize}
                      onChange={(e) => setEditGripSize(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">Not specified</option>
                      <option value="undersize">Undersize</option>
                      <option value="standard">Standard</option>
                      <option value="midsize">Midsize</option>
                      <option value="jumbo">Jumbo</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Material</label>
                    <input
                      type="text"
                      value={editGripMaterial}
                      onChange={(e) => setEditGripMaterial(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="e.g., Rubber, Corded Rubber"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Wraps</label>
                    <select
                      value={editGripWraps}
                      onChange={(e) => setEditGripWraps(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="0 wraps">0 wraps (Standard)</option>
                      <option value="+1 wrap">+1 wrap</option>
                      <option value="+2 wraps">+2 wraps</option>
                      <option value="+3 wraps">+3 wraps</option>
                      <option value="+4 wraps">+4 wraps</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Club Head Specifications - Only for Drivers/Woods/Hybrids */}
              {editingClub && (editingClub.type === 'driver' || editingClub.type.includes('wood') || editingClub.type.includes('hybrid')) && (
                <div className="border-b border-gray-200 pb-4">
                  <h4 className="font-semibold text-lg text-gray-800 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5 text-orange-600" />
                    Club Head Specifications
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Swing Weight</label>
                      <input
                        type="text"
                        value={editSwingWeight}
                        onChange={(e) => setEditSwingWeight(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="e.g., D1, D2, D3"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Head Volume</label>
                      <input
                        type="text"
                        value={editHeadVolume}
                        onChange={(e) => setEditHeadVolume(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="e.g., 125cc, 460cc"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Adjustability Settings - Only for Drivers/Woods/Hybrids */}
              {editingClub && (editingClub.type === 'driver' || editingClub.type.includes('wood') || editingClub.type.includes('hybrid')) && (
              <div className="border-b border-gray-200 pb-4">
                <h4 className="font-semibold text-lg text-gray-800 mb-3 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-indigo-600" />
                  Adjustability Settings
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Loft Adjustment</label>
                    <select
                      value={editLoftAdjustment}
                      onChange={(e) => setEditLoftAdjustment(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="Standard">Standard</option>
                      <option value="-2°">-2°</option>
                      <option value="-1°">-1°</option>
                      <option value="+1°">+1°</option>
                      <option value="+2°">+2°</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lie Adjustment</label>
                    <select
                      value={editLieAdjustment}
                      onChange={(e) => setEditLieAdjustment(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="Standard">Standard</option>
                      <option value="-2°">-2° Flat</option>
                      <option value="-1°">-1° Flat</option>
                      <option value="+1°">+1° Upright</option>
                      <option value="+2°">+2° Upright</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Face Angle</label>
                    <select
                      value={editFaceAngle}
                      onChange={(e) => setEditFaceAngle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="Square">Square</option>
                      <option value="1° Open">1° Open</option>
                      <option value="0.5° Open">0.5° Open</option>
                      <option value="0.5° Closed">0.5° Closed</option>
                      <option value="1° Closed">1° Closed</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Weight Configuration</label>
                    <select
                      value={editWeightConfig}
                      onChange={(e) => setEditWeightConfig(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="Neutral">Neutral</option>
                      <option value="Draw bias">Draw Bias</option>
                      <option value="Fade bias">Fade Bias</option>
                      <option value="Low spin">Low Spin</option>
                      <option value="High launch">High Launch</option>
                    </select>
                  </div>
                </div>
              </div>
              )}

              {/* Pro Tip */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
                <p className="text-sm text-green-800">
                  <span className="font-semibold">💡 Pro Tip:</span> The more details you add, the more accurate our AI recommendations and distance calculations will be!
                </p>
              </div>

                {/* Action Buttons */}
                <div className="space-y-3 pt-2">
                  <div className="flex gap-3">
                    <button
                      onClick={() => saveClubDetailsEdit(false)}
                      className="flex-1 bg-gradient-to-r from-green-600 to-green-500 text-white px-6 py-3 rounded-lg hover:from-green-700 hover:to-green-600 font-semibold transition-all shadow-lg"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingClubDetails(null)}
                      className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                  <button
                    onClick={() => handleDeleteClub(editingClubDetails!)}
                    className="w-full bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 font-semibold transition-all border-2 border-red-700"
                  >
                    🗑️ Remove from Bag
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Distance Recalculation Prompt Modal */}
      {showRecalcPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-2">
              <RefreshCw className="w-6 h-6 text-green-600" />
              Recalculate Distances?
            </h3>
            <p className="text-gray-700 mb-6">
              Your club specs changed (loft, shaft, or flex). Do you want GolfIQ to automatically recalculate your distances based on the new specifications?
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleRecalcYes}
                disabled={isRecalculating}
                className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRecalculating ? 'Recalculating...' : 'Yes, recalc automatically'}
              </button>
              <button
                onClick={handleRecalcNo}
                disabled={isRecalculating}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-all disabled:opacity-50"
              >
                No, I'll edit manually
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Capture Modal */}
      {showPhotoCapture && (
        <div className="fixed inset-0 z-[60]">
          <ClubPhotoCapture
            onClubIdentified={handlePhotoClubIdentified}
            onCancel={() => {
              setShowPhotoCapture(false);
              setPhotoCaptureMode(null);
            }}
          />
        </div>
      )}

      {/* Add Club Modal */}
      {showAddClubModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowAddClubModal(false)}
        >
          <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Plus className="w-6 h-6 text-green-600" />
                Add New Club
              </h3>
              <button
                onClick={() => setShowAddClubModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Photo Capture Quick Action */}
            <button
              onClick={() => {
                setShowAddClubModal(false);
                setPhotoCaptureMode('add');
                setShowPhotoCapture(true);
              }}
              className="w-full flex items-center gap-3 p-4 mb-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl hover:border-green-400 hover:shadow-md transition-all"
            >
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-green-800 text-sm">Snap a Photo to Auto-Identify</p>
                <p className="text-green-600 text-xs">AI reads your club head and shaft label</p>
              </div>
              <ArrowRight className="w-5 h-5 text-green-400 flex-shrink-0" />
            </button>

            <div className="space-y-4">
              {/* Club Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Club Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={newClubType}
                  onChange={(e) => setNewClubType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Select Club Type</option>
                  <optgroup label="Woods">
                    <option value="driver">Driver</option>
                    <option value="3-wood">3-Wood</option>
                    <option value="4-wood">4-Wood</option>
                    <option value="5-wood">5-Wood</option>
                    <option value="7-wood">7-Wood</option>
                    <option value="9-wood">9-Wood</option>
                  </optgroup>
                  <optgroup label="Hybrids">
                    <option value="2-hybrid">2-Hybrid</option>
                    <option value="3-hybrid">3-Hybrid</option>
                    <option value="4-hybrid">4-Hybrid</option>
                    <option value="5-hybrid">5-Hybrid</option>
                    <option value="6-hybrid">6-Hybrid</option>
                    <option value="7-hybrid">7-Hybrid</option>
                  </optgroup>
                  <optgroup label="Irons">
                    <option value="3-iron">3-Iron</option>
                    <option value="4-iron">4-Iron</option>
                    <option value="5-iron">5-Iron</option>
                    <option value="6-iron">6-Iron</option>
                    <option value="7-iron">7-Iron</option>
                    <option value="8-iron">8-Iron</option>
                    <option value="9-iron">9-Iron</option>
                  </optgroup>
                  <optgroup label="Wedges">
                    <option value="pw">Pitching Wedge (PW)</option>
                    <option value="aw">Approach Wedge (AW)</option>
                    <option value="gw">Gap Wedge (GW)</option>
                    <option value="sw">Sand Wedge (SW)</option>
                    <option value="lw">Lob Wedge (LW)</option>
                  </optgroup>
                  <optgroup label="Putter">
                    <option value="putter">Putter</option>
                  </optgroup>
                </select>
              </div>

              {/* Loft - Important for wedges */}
              {newClubType && ['pw', 'aw', 'gw', 'sw', 'lw'].includes(newClubType) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Loft (degrees) <span className="text-green-600 text-xs ml-1">*Important for wedges</span>
                  </label>
                  <select
                    value={newClubLoft}
                    onChange={(e) => setNewClubLoft(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Select Loft</option>
                    {[44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 64].map(loft => (
                      <option key={loft} value={loft}>{loft}°</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Accurate loft helps with gapping recommendations
                  </p>
                </div>
              )}

              {/* Brand (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand <span className="text-gray-400 text-xs">(optional)</span>
                </label>
                <select
                  value={newClubBrand}
                  onChange={(e) => setNewClubBrand(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Select Brand</option>
                  {GOLF_BRANDS.map(brand => (
                    <option key={brand.id} value={brand.name}>{brand.name}</option>
                  ))}
                </select>
              </div>

              {/* Model (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model <span className="text-gray-400 text-xs">(optional)</span>
                </label>
                {(() => {
                  const brandId = newClubBrand.toLowerCase().replace(/\s+/g, '');
                  const category = newClubType ? clubTypeToCategory(newClubType) : 'irons';
                  const models = getModelsForBrand(brandId, category);
                  
                  if (models.length > 0) {
                    return (
                      <>
                        <select
                          value={newClubModel}
                          onChange={(e) => {
                            setNewClubModel(e.target.value);
                            if (e.target.value !== "__OTHER__") {
                              setNewClubCustomModel("");
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="">Select Model</option>
                          {models.map(model => (
                            <option key={model} value={model}>{model}</option>
                          ))}
                          <option value="__OTHER__">Other / Custom</option>
                        </select>
                        {newClubModel === "__OTHER__" && (
                          <input
                            type="text"
                            value={newClubCustomModel}
                            onChange={(e) => setNewClubCustomModel(e.target.value)}
                            className="w-full px-3 py-2 mt-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            placeholder="Enter model name (e.g., AP2, Eye2, M3)"
                            autoFocus
                          />
                        )}
                      </>
                    );
                  }
                  
                  return (
                    <input
                      type="text"
                      value={newClubModel}
                      onChange={(e) => setNewClubModel(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="e.g., Stealth 2, P770, SM9"
                    />
                  );
                })()}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleAddClub}
                  disabled={!newClubType}
                  className="flex-1 bg-gradient-to-r from-green-600 to-green-500 text-white px-6 py-3 rounded-lg hover:from-green-700 hover:to-green-600 font-semibold transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-5 h-5 inline mr-2" />
                  Add Club
                </button>
                <button
                  onClick={() => setShowAddClubModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default MyGolfBag;

